import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft, Upload, FileSpreadsheet, Search, Image as ImageIcon, RefreshCw, Trash2 } from "lucide-react";
import CamperPhoto from "../components/CamperPhoto";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CamperPhotosView({ onBack }) {
  const [stats, setStats] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [campers, setCampers] = useState([]);
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(null); // camper_id currently uploading
  const [photoVersions, setPhotoVersions] = useState({}); // camper_id -> bump to refresh preview
  const csvInputRef = useRef(null);
  const fileInputsRef = useRef({});

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const loadStats = async () => {
    try {
      const r = await axios.get(`${API}/admin/photos/stats`, { headers });
      setStats(r.data);
    } catch { /* non-critical */ }
  };

  const loadCampers = async (q = "") => {
    try {
      const r = await axios.get(`${API}/campers`, { headers, params: { search: q } });
      setCampers(r.data || []);
    } catch {
      toast.error("Failed to load campers");
    }
  };

  useEffect(() => {
    loadStats();
    loadCampers("");
  }, []);

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/photos/import-csv`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
        timeout: 600000, // 10 min — 900 photos × ~0.5s each
      });
      setImportResult(r.data);
      toast.success(`Imported ${r.data.imported} of ${r.data.rows_seen} photos`);
      await loadStats();
      // Bust all photo caches so cards re-fetch
      setPhotoVersions((v) => ({ ...v, __all: (v.__all || 0) + 1 }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CSV import failed");
    } finally {
      setImporting(false);
      if (csvInputRef.current) csvInputRef.current.value = "";
    }
  };

  const handleSinglePhoto = async (camperId, file) => {
    if (!file) return;
    setUploading(camperId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/admin/photos/${camperId}`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded");
      setPhotoVersions((v) => ({ ...v, [camperId]: (v[camperId] || 0) + 1 }));
      await loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  const handleDeletePhoto = async (camperId) => {
    if (!window.confirm("Remove this camper's photo?")) return;
    try {
      await axios.delete(`${API}/admin/photos/${camperId}`, { headers });
      toast.success("Photo removed");
      setPhotoVersions((v) => ({ ...v, [camperId]: (v[camperId] || 0) + 1 }));
      await loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = campers.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const full = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
    return full.includes(q) || (c.group_code || "").toLowerCase().includes(q);
  }).slice(0, 60); // cap render to keep page fast

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="photos-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
        </Button>
        <div>
          <h2 className="font-heading font-bold text-lg flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-indigo-600" /> Camper Photos
          </h2>
          <p className="text-xs text-muted-foreground">
            Bulk import from CampMinder CSV or upload individual photos.
          </p>
        </div>
      </div>

      {/* Stats + Bulk CSV import */}
      <Card>
        <CardContent className="p-4 space-y-4">
          {stats && (
            <div className="grid grid-cols-3 gap-2" data-testid="photos-stats">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-center">
                <p className="text-2xl font-extrabold text-slate-800">{stats.total_campers}</p>
                <p className="text-[10px] uppercase tracking-wide text-slate-600">Total Campers</p>
              </div>
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
                <p className="text-2xl font-extrabold text-emerald-800">{stats.with_photo}</p>
                <p className="text-[10px] uppercase tracking-wide text-emerald-700">With Photo</p>
              </div>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-center">
                <p className="text-2xl font-extrabold text-amber-800">{stats.missing}</p>
                <p className="text-[10px] uppercase tracking-wide text-amber-700">Missing</p>
              </div>
            </div>
          )}

          <div className="border-2 border-dashed border-indigo-200 rounded-lg p-4 bg-indigo-50/40">
            <div className="flex items-center gap-3 mb-2">
              <FileSpreadsheet className="w-5 h-5 text-indigo-700" />
              <p className="text-sm font-bold text-indigo-900">Bulk Import from CampMinder CSV</p>
            </div>
            <p className="text-xs text-indigo-800 mb-3">
              Upload the CSV with columns <code className="bg-white px-1 rounded">PersonID, Last Name, First Name, Photo</code>. Photos are downloaded automatically from CampMinder's CDN. ~900 photos takes ~5 minutes.
            </p>
            <div className="flex items-center gap-2">
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv"
                onChange={handleCsvUpload}
                disabled={importing}
                className="text-xs"
                data-testid="photos-csv-upload-input"
              />
              {importing && (
                <span className="text-xs text-indigo-700 flex items-center gap-1">
                  <RefreshCw className="w-3 h-3 animate-spin" /> Importing…
                </span>
              )}
            </div>
            {importResult && (
              <div className="mt-3 p-2 bg-white rounded border border-indigo-200 text-xs space-y-0.5" data-testid="photos-import-result">
                <p><strong>{importResult.imported}</strong> imported of {importResult.rows_seen} rows</p>
                {importResult.skipped_no_camper_match > 0 && (
                  <p className="text-amber-700">{importResult.skipped_no_camper_match} skipped (no matching camper in DB)</p>
                )}
                {importResult.skipped_no_photo_url > 0 && (
                  <p className="text-amber-700">{importResult.skipped_no_photo_url} skipped (no photo URL in row)</p>
                )}
                {importResult.failed_download > 0 && (
                  <p className="text-red-700">{importResult.failed_download} failed to download</p>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Per-camper upload + preview */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Upload className="w-4 h-4 text-slate-700" />
            <h3 className="text-sm font-bold">Upload Individual Photo</h3>
          </div>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search camper by name or group…"
              className="pl-9"
              data-testid="photos-camper-search"
            />
          </div>
          <p className="text-[11px] text-muted-foreground">
            Showing {filtered.length} of {campers.length} campers{search ? ` matching "${search}"` : ""} {filtered.length === 60 && "(limited)"}
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {filtered.map((c) => (
              <div
                key={c.id}
                className="flex flex-col items-center gap-2 p-3 border rounded-lg bg-white"
                data-testid={`photos-camper-row-${c.id}`}
              >
                <CamperPhoto
                  key={`${c.id}-${photoVersions[c.id] || 0}-${photoVersions.__all || 0}`}
                  camperId={c.id}
                  firstName={c.first_name}
                  lastName={c.last_name}
                  size={72}
                  rounded="full"
                />
                <div className="text-center">
                  <p className="text-xs font-semibold text-gray-900 leading-tight">{c.first_name} {c.last_name}</p>
                  <p className="text-[10px] text-slate-500">{c.group_code}</p>
                </div>
                <div className="flex items-center gap-1">
                  <input
                    ref={(el) => { fileInputsRef.current[c.id] = el; }}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleSinglePhoto(c.id, e.target.files?.[0])}
                    className="hidden"
                    data-testid={`photos-upload-input-${c.id}`}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11px] px-2"
                    disabled={uploading === c.id}
                    onClick={() => fileInputsRef.current[c.id]?.click()}
                    data-testid={`photos-upload-btn-${c.id}`}
                  >
                    {uploading === c.id ? "…" : "Upload"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-red-600 hover:text-red-700"
                    onClick={() => handleDeletePhoto(c.id)}
                    data-testid={`photos-delete-btn-${c.id}`}
                    title="Remove photo"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
